import { USER_EVENTS } from '@sm/metrics';
import { merge, isEqual } from 'lodash';
import { OECTagType, OECTrainingStatus } from './types';
import { retrainStepsHaveBeenCompleted } from './ManualTagging/AutoTagging/lib';
import { RESPONSE_PAGE_SIZE, MIN_NUMBER_OF_THEMES, PERCENT_TO_TAG, NUMBER_OF_RESPONSES, SIGNIFICANT_CHANGES_FOR_RETRAIN } from './constants';
export const updateTheme = ({
  themes,
  themeId,
  name
}) => themes.map(theme => {
  if (theme.id === themeId) {
    return Object.assign(Object.assign({}, theme), {
      theme: name
    });
  }
  return theme;
});
export const createKeywords = ({
  theme
}) => {
  return {
    themeId: theme.id,
    keywords: []
  };
};
export const hasKeywordsPresent = keywords => {
  return keywords.length > 0 && keywords.some(k => k.keywords.length > 0);
};
export const deleteTheme = ({
  themes,
  themeId
}) => themes.filter(theme => theme.id !== themeId);
export const deleteKeywords = ({
  keywords,
  themeId
}) => keywords.filter(k => k.themeId !== themeId);
export const removeResponseFromTheme = ({
  themes,
  themeIds,
  responseTextId
}) => themes.map(theme => {
  var _a;
  // manually added tag has been removed
  if (themeIds.includes(theme.id)) {
    return Object.assign(Object.assign({}, theme), {
      responseTextIds: theme.responseTextIds.filter(r => r !== responseTextId)
    });
  }
  // autogenerated tag has been removed
  const autogeneratedTagId = (_a = theme.autogeneratedTag) === null || _a === void 0 ? void 0 : _a.id;
  if (theme.autogeneratedTag && autogeneratedTagId && themeIds.includes(autogeneratedTagId)) {
    const autogeneratedTag = {
      id: autogeneratedTagId,
      responseTextIds: theme.autogeneratedTag.responseTextIds.filter(r => r !== responseTextId)
    };
    return Object.assign(Object.assign({}, theme), {
      autogeneratedTag
    });
  }
  return theme;
});
export const addResponseToTheme = ({
  themes,
  themeIds,
  responseTextId
}) => themes.map(theme => {
  var _a;
  // manually added tag has been added
  if (themeIds.includes(theme.id)) {
    return Object.assign(Object.assign({}, theme), {
      responseTextIds: [...theme.responseTextIds, responseTextId]
    });
  }
  // autogenerated tag has been added
  const autogeneratedTagId = (_a = theme.autogeneratedTag) === null || _a === void 0 ? void 0 : _a.id;
  if (theme.autogeneratedTag && autogeneratedTagId && themeIds.includes(autogeneratedTagId)) {
    const autogeneratedTag = {
      id: autogeneratedTagId,
      responseTextIds: [...theme.autogeneratedTag.responseTextIds, responseTextId]
    };
    return Object.assign(Object.assign({}, theme), {
      autogeneratedTag
    });
  }
  return theme;
});
export const buildThemesForResponse = ({
  response,
  themes
}) => {
  return response.tagIds.reduce((result, tagId) => {
    var _a;
    // manually generated theme
    const theme = themes.find(t => t.id === tagId);
    if (theme) {
      result.push(theme);
    }
    // autogenerated theme
    const autogenerated = themes.find(t => {
      var _a;
      return ((_a = t.autogeneratedTag) === null || _a === void 0 ? void 0 : _a.id) === tagId;
    });
    const autogeneratedTagId = (_a = autogenerated === null || autogenerated === void 0 ? void 0 : autogenerated.autogeneratedTag) === null || _a === void 0 ? void 0 : _a.id;
    if (autogenerated && autogeneratedTagId) {
      result.push(Object.assign(Object.assign({}, autogenerated), {
        tagType: OECTagType.OECMODEL,
        id: autogeneratedTagId
      }));
    }
    return result;
  }, []);
};
export const removeThemesFromResponse = ({
  responses,
  themeId,
  responseTextId
}) => responses.map(response => {
  // Different response
  if (responseTextId !== response.responseTextId) {
    return response;
  }
  // Filter out the tag that has been deleted
  const newTagIds = response.tagIds.filter(t => t !== themeId);
  return Object.assign(Object.assign({}, response), {
    tagIds: newTagIds
  });
});
export const addThemesToResponse = ({
  responses,
  themeIds,
  responseTextId
}) => responses.map(response => {
  // Different response
  if (responseTextId !== response.responseTextId) {
    return response;
  }
  // Get the new Tag IDs
  const newTagIds = themeIds.filter(themeId => !response.tagIds.includes(themeId));
  return Object.assign(Object.assign({}, response), {
    tagIds: [...response.tagIds, ...newTagIds]
  });
});
export const filterCheckedThemesBySearch = ({
  searchPhrase,
  themes
}) => themes.map(theme => {
  const show = theme.theme.toLowerCase().includes(searchPhrase.toLowerCase());
  return Object.assign(Object.assign({}, theme), {
    show
  });
});
export const splitBySearchPhrase = ({
  searchPhrase,
  text
}) => {
  const re = new RegExp(`(${searchPhrase})`, 'i');
  const textLower = text.toLowerCase();
  const searchPhraseLower = searchPhrase.toLowerCase();
  // No point searching for empty string
  if (searchPhrase === '') {
    return [text];
  }
  // Return early if it's not a substring
  if (!textLower.includes(searchPhraseLower)) {
    return [text];
  }
  return text.split(re).filter(Boolean);
};
export const transformKeywords = ({
  keywords
}) => keywords.map(k => {
  const newKeywords = k.keywords.join(', ');
  return Object.assign(Object.assign({}, k), {
    keywords: newKeywords,
    error: false,
    errorMessages: []
  });
});
export const transformBackKeywords = ({
  keywords
}) => keywords.map(k => {
  const newKeywords = k.keywords.split(',').map(keyword => keyword.trim());
  return {
    themeId: k.themeId,
    keywords: newKeywords
  };
});
export const formatTheme = theme => {
  return {
    id: theme.id,
    theme: theme.label,
    tagType: theme.tagType,
    responseTextIds: theme.responseTextIds,
    autogeneratedTag: theme.autogeneratedTag ? {
      id: theme.autogeneratedTag.id,
      responseTextIds: theme.autogeneratedTag.responseTextIds
    } : null
  };
};
export const getResponseCountForTheme = theme => {
  var _a, _b;
  const manuallyTagged = theme.responseTextIds;
  const autotagged = (_b = (_a = theme.autogeneratedTag) === null || _a === void 0 ? void 0 : _a.responseTextIds) !== null && _b !== void 0 ? _b : [];
  return manuallyTagged.length + autotagged.length;
};
export const formatResponse = ({
  id,
  stimulus,
  respondentId,
  questionId,
  optionId,
  content,
  createdAt,
  tagIds,
  sentiment
}) => {
  return {
    responseTextId: id,
    stimulus,
    respondentId,
    questionId,
    optionId,
    content,
    date: createdAt,
    tagIds,
    sentiment,
    suggestedTags: []
  };
};
export const formatKeywords = keywords => {
  return {
    themeId: keywords.id,
    keywords: keywords.keywords
  };
};
export const formatKeywordsForPersistence = ({
  themes,
  keywords
}) => {
  return themes.map(t => {
    var _a;
    const k = (_a = keywords.find(kw => kw.themeId === t.id)) === null || _a === void 0 ? void 0 : _a.keywords;
    return {
      id: t.id,
      label: t.theme,
      keywords: k !== null && k !== void 0 ? k : []
    };
  });
};
export const validateKeywordsForTheme = ({
  keywords,
  newKeywords,
  themeId
}) => {
  // this is a new theme
  const keywordsIdx = keywords.findIndex(kw => kw.themeId === themeId);
  if (keywordsIdx === -1) {
    return [...keywords, {
      themeId,
      keywords: newKeywords,
      error: false,
      errorMessages: []
    }];
  }
  // eslint-disable-next-line no-param-reassign
  keywords[keywordsIdx] = {
    themeId,
    keywords: newKeywords,
    error: false,
    errorMessages: []
  };
  return keywords;
};
export const trackAmplitudeEvent = ({
  amplitudeEvent,
  name = USER_EVENTS.ELEMENT_INTERACTION,
  data = {}
}) => {
  if (window.SM && (window === null || window === void 0 ? void 0 : window.SM.MetricsTracker)) {
    const hasSubscribers = !!window.SM.MetricsTracker.getSubscribers().length;
    if (hasSubscribers) {
      window.SM.MetricsTracker.track({
        name,
        data: Object.assign({
          amplitudeEvent
        }, data)
      });
    }
  }
};
export const getResponseThemeIds = ({
  responses,
  responseTextId
}) => {
  var _a, _b;
  return (_b = (_a = responses.find(r => r.responseTextId === responseTextId)) === null || _a === void 0 ? void 0 : _a.tagIds) !== null && _b !== void 0 ? _b : [];
};
export const getPredictedThemes = ({
  themes,
  responseThemes
}) => {
  return themes.filter(t => t.autogeneratedTag && responseThemes.includes(t.autogeneratedTag.id)).map(t => t.theme);
};
export const getThemeNames = ({
  themes,
  themeIds
}) => {
  return themes.filter(t => themeIds.includes(t.id) || t.autogeneratedTag && themeIds.includes(t.autogeneratedTag.id)).map(t => t.theme);
};
export const getResponseText = ({
  responses,
  responseTextId
}) => {
  var _a, _b;
  return (_b = (_a = responses.find(r => r.responseTextId === responseTextId)) === null || _a === void 0 ? void 0 : _a.content) !== null && _b !== void 0 ? _b : '';
};
export const getCombinedItems = (prevItems, newItems) => {
  const existingKeysSet = new Set(prevItems.map(item => item.id));
  return newItems.reduce((items, item) => {
    if (!existingKeysSet.has(item.id)) {
      items.push(item);
    }
    return items;
  }, prevItems.slice() || []);
};
export const getPaginatedResponses = ({
  responsesPage,
  responses
}) => {
  const firstResponseIndex = responsesPage * RESPONSE_PAGE_SIZE;
  const lastResponseIndex = firstResponseIndex + RESPONSE_PAGE_SIZE;
  return responses.slice(firstResponseIndex, lastResponseIndex);
};
export const getThemesPerResponse = ({
  responses
}) => {
  return responses.reduce((acc, r) => Object.assign(Object.assign({}, acc), {
    [r.responseTextId]: r.tagIds
  }), {});
};
export const updateResponsesThemes = ({
  responses,
  themesPerResponse
}) => {
  return responses.map(response => {
    const tagIds = themesPerResponse[response.id];
    if (tagIds) {
      return Object.assign(Object.assign({}, response), {
        tagIds
      });
    }
    return response;
  });
};
export const mergePaginatedResponses = ({
  prevData,
  newData,
  themesPerResponse
}) => {
  // update the cached responses with any tags since modified by the user
  const updatedPrevResponses = updateResponsesThemes({
    responses: prevData === null || prevData === void 0 ? void 0 : prevData.getOecResponses.items,
    themesPerResponse
  });
  // combine any new responses fetched with the previously cached responses
  const items = getCombinedItems(updatedPrevResponses, newData === null || newData === void 0 ? void 0 : newData.getOecResponses.items);
  // merge all new data except for the responses
  const merged = merge({}, prevData, newData);
  return {
    getOecResponses: Object.assign(Object.assign({}, merged.getOecResponses), {
      items
    })
  };
};
export const isAutoTaggingComplete = trainingStatus => {
  return [OECTrainingStatus.REQUIRES_RETRAINING, OECTrainingStatus.TRAINING_COMPLETE].includes(trainingStatus);
};
export const haveKeywordsChanged = ({
  generatedKeywords,
  unsavedKeywords
}) => {
  const generated = generatedKeywords.map(({
    themeId,
    keywords
  }) => {
    return {
      id: themeId,
      keywords
    };
  });
  const unsaved = unsavedKeywords.map(({
    themeId,
    keywords
  }) => {
    return {
      id: themeId,
      keywords
    };
  });
  return !isEqual(generated, unsaved);
};
export const areThemesEqual = ({
  prevThemes,
  newThemes
}) =>
// just using lodash for now; will probably need to add some custom checks in future
isEqual(prevThemes.map(t => t.id), newThemes.map(t => t.id));
export const areThemesAdded = ({
  prevThemes,
  newThemes
}) => {
  const prevThemeIds = prevThemes.map(t => t.id);
  const newThemeIds = newThemes.map(t => t.id);
  const difference = newThemeIds.filter(t => !prevThemeIds.includes(t));
  return difference.length > 0;
};
export const areThemesDeleted = ({
  prevThemes,
  newThemes
}) => {
  const prevThemeIds = prevThemes.map(t => t.id);
  const newThemeIds = newThemes.map(t => t.id);
  const difference = prevThemeIds.filter(t => !newThemeIds.includes(t));
  return difference.length > 0;
};
// helper used for a few different methods
export const responsesPerThemeReducer = ({
  themes
}) => themes.reduce((acc, t) => Object.assign(Object.assign(Object.assign({}, acc), {
  [t.id]: t.responseTextIds
}), t.autogeneratedTag && {
  [t.autogeneratedTag.id]: t.autogeneratedTag.responseTextIds
}), {});
export const areResponsesEqual = ({
  prevThemes,
  newThemes
}) => {
  return isEqual(responsesPerThemeReducer({
    themes: prevThemes
  }), responsesPerThemeReducer({
    themes: newThemes
  }));
};
export const significantChangeCountResponses = ({
  prevThemes,
  newThemes
}) => {
  const prevResponsesPerTheme = responsesPerThemeReducer({
    themes: prevThemes
  });
  const newResponsesPerTheme = responsesPerThemeReducer({
    themes: newThemes
  });
  return Object.keys(prevResponsesPerTheme).reduce((acc, themeId) => {
    const prevResponses = prevResponsesPerTheme[themeId];
    const newResponses = newResponsesPerTheme[themeId];
    if (!prevResponses || !newResponses) {
      return acc;
    }
    // get the symmetric difference in themes per response and add the count to the running total
    return acc + prevResponses.filter(x => !newResponses.includes(x)).concat(newResponses.filter(x => !prevResponses.includes(x))).length;
  }, 0);
};
// TODO: This function is getting quite complicated.
// At a later date we might try and rearchitect more of this
// logic and move to the backend.
export const isRetrainAllowed = ({
  trainingStatus,
  themesAdded,
  themesDeleted,
  significantChangesResponses,
  notEnoughThemesCreated,
  notEnoughThemesCreatedStep
}) => {
  // checks required to be completed before a user can retrain
  if (notEnoughThemesCreated) {
    return false;
  }
  // user is in a retrain state (trust the backend)
  if (trainingStatus === OECTrainingStatus.REQUIRES_RETRAINING) {
    return true;
  }
  // the user has completed autotagging steps and can continue with retraining
  if (retrainStepsHaveBeenCompleted([notEnoughThemesCreatedStep])) {
    return true;
  }
  // themes added or deleted and checks pass
  if (themesAdded || themesDeleted) {
    return true;
  }
  // significant changes to responses and checks pass
  if (significantChangesResponses > SIGNIFICANT_CHANGES_FOR_RETRAIN) {
    return true;
  }
  return false;
};
export const hasInsufficientResponses = (themes, responsesTotal) => {
  if (themes.length === 0) {
    return true;
  }
  // number of responses that need to be tagged
  const amountToTag = Math.ceil(responsesTotal * (PERCENT_TO_TAG / 100.0));
  // get number of responses that have a tag applied
  const responsesIdsTagged = Array.from(new Set(themes.flatMap(t => {
    var _a, _b;
    return [...t.responseTextIds, ...((_b = (_a = t.autogeneratedTag) === null || _a === void 0 ? void 0 : _a.responseTextIds) !== null && _b !== void 0 ? _b : [])];
  })));
  return responsesIdsTagged.length < amountToTag;
};
export const hasInsufficientThemes = themes => {
  if (themes.length === 0) {
    return true;
  }
  return themes.filter(t => {
    var _a;
    const manuallyAdded = t.responseTextIds.length;
    const autogenerated = (_a = t.autogeneratedTag) === null || _a === void 0 ? void 0 : _a.responseTextIds.length;
    return manuallyAdded + (autogenerated !== null && autogenerated !== void 0 ? autogenerated : 0) < NUMBER_OF_RESPONSES;
  }).length > 0;
};
export const getStatus = ({
  themes
}) => {
  const autoTaggingStatus = {
    // there must be at least 2 themes added
    notEnoughThemesCreated: themes.length < MIN_NUMBER_OF_THEMES
  };
  return autoTaggingStatus;
};
export const getRetrainData = ({
  prevThemes,
  newThemes
}) => {
  return {
    themesChanged: !areThemesEqual({
      prevThemes,
      newThemes
    }),
    responsesChanged: !areResponsesEqual({
      prevThemes,
      newThemes
    }),
    themesAdded: areThemesAdded({
      prevThemes,
      newThemes
    }),
    themesDeleted: areThemesDeleted({
      prevThemes,
      newThemes
    }),
    significantChangesResponses: significantChangeCountResponses({
      prevThemes,
      newThemes
    })
  };
};
export const mergeSelectedIdsAndAutoGeneratedIds = (themes, selectedThemes) => {
  // there are manually generated themes and autogenerated themes.
  // these are essentially duplicates but we only use the manually
  // generated themes for the filter. we also need to include the
  // autogenerated IDs when making the request to the backend.
  const autoGeneratedIds = themes.reduce((result, theme) => {
    var _a;
    const selected = selectedThemes.includes(theme.id);
    const autogeneratedId = (_a = theme.autogeneratedTag) === null || _a === void 0 ? void 0 : _a.id;
    if (selected && autogeneratedId) {
      return [...result, autogeneratedId];
    }
    return result;
  }, []);
  return [...selectedThemes, ...autoGeneratedIds];
};